import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

import {
  errorDeleteItem,
  errorItemData,
  errorListData,
  errorPostPutItemData,
  receiveDeleteItem,
  receiveItemData,
  receiveListData,
  receivePostPutItemData,
  requestDeleteItem,
  requestItemData,
  requestListData,
  requestPostPutItemData,
} from '@/common/utils/state.utils'

import { IStripeTerminal } from 'src/features/stripe-terminals/interfaces/stripe-terminal.interface'

import { stripeTerminalsService } from './stripe-terminals.service'
import { StripeTerminalsState } from './stripe-terminals.state.interface'

const initialState = new StripeTerminalsState()

const stripeTerminalsSlice = createSlice({
  name: 'stripeTerminals',
  initialState,
  reducers: {
    updateStripeTerminals: (state, action: PayloadAction<IStripeTerminal[]>) => {
      state.stripeTerminals.data = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      // fetch stripe terminals
      .addCase(stripeTerminalsService.fetchStripeTerminals.pending, (state) => {
        return requestListData<StripeTerminalsState, IStripeTerminal[]>({ ...state }, ['stripeTerminals'])
      })
      .addCase(stripeTerminalsService.fetchStripeTerminals.fulfilled, (state, action) => {
        return receiveListData<StripeTerminalsState, IStripeTerminal[]>(
          { ...state },
          ['stripeTerminals'],
          action.payload,
        )
      })
      .addCase(stripeTerminalsService.fetchStripeTerminals.rejected, (state, action) => {
        return errorListData<StripeTerminalsState, IStripeTerminal[]>(
          { ...state },
          ['stripeTerminals'],
          String(action.error.message),
        )
      })

      // fetch stripe terminal
      .addCase(stripeTerminalsService.fetchStripeTerminal.pending, (state) => {
        return requestItemData<StripeTerminalsState, IStripeTerminal>({ ...state }, ['stripeTerminal'])
      })
      .addCase(stripeTerminalsService.fetchStripeTerminal.fulfilled, (state, action) => {
        return receiveItemData<StripeTerminalsState, IStripeTerminal>(
          { ...state },
          ['stripeTerminal'],
          action.payload.item,
        )
      })
      .addCase(stripeTerminalsService.fetchStripeTerminal.rejected, (state, action) => {
        return errorItemData<StripeTerminalsState, IStripeTerminal>(
          { ...state },
          ['stripeTerminal'],
          String(action.error.message),
        )
      })

      // create stripe terminal
      .addCase(stripeTerminalsService.createStripeTerminal.pending, (state) => {
        return requestPostPutItemData<StripeTerminalsState, IStripeTerminal>({ ...state }, ['stripeTerminalCreate'])
      })
      .addCase(stripeTerminalsService.createStripeTerminal.fulfilled, (state, action) => {
        return receivePostPutItemData<StripeTerminalsState, IStripeTerminal>(
          { ...state },
          ['stripeTerminalCreate'],
          action.payload,
        )
      })
      .addCase(stripeTerminalsService.createStripeTerminal.rejected, (state, action) => {
        return errorPostPutItemData<StripeTerminalsState, IStripeTerminal>(
          { ...state },
          ['stripeTerminalCreate'],
          String(action.error.message),
        )
      })

      // update stripe terminal
      .addCase(stripeTerminalsService.updateStripeTerminal.pending, (state) => {
        return requestPostPutItemData<StripeTerminalsState, IStripeTerminal>({ ...state }, ['stripeTerminalUpdate'])
      })
      .addCase(stripeTerminalsService.updateStripeTerminal.fulfilled, (state, action) => {
        return receivePostPutItemData<StripeTerminalsState, IStripeTerminal>(
          { ...state },
          ['stripeTerminalUpdate'],
          action.payload,
        )
      })
      .addCase(stripeTerminalsService.updateStripeTerminal.rejected, (state, action) => {
        return errorPostPutItemData<StripeTerminalsState, IStripeTerminal>(
          { ...state },
          ['stripeTerminalUpdate'],
          String(action.error.message),
        )
      })

      // delete stripe terminal
      .addCase(stripeTerminalsService.deleteStripeTerminal.pending, (state) => {
        return requestDeleteItem<StripeTerminalsState, IStripeTerminal>({ ...state }, ['stripeTerminalDelete'])
      })
      .addCase(stripeTerminalsService.deleteStripeTerminal.fulfilled, (state) => {
        return receiveDeleteItem<StripeTerminalsState, IStripeTerminal>({ ...state }, ['stripeTerminalDelete'])
      })
      .addCase(stripeTerminalsService.deleteStripeTerminal.rejected, (state, action) => {
        return errorDeleteItem<StripeTerminalsState, IStripeTerminal>(
          { ...state },
          ['stripeTerminalDelete'],
          String(action.error.message),
        )
      })

      // upload stripe terminal media
      .addCase(stripeTerminalsService.uploadStripeTerminalMedia.pending, (state) => {
        return requestPostPutItemData<StripeTerminalsState, any>({ ...state }, ['stripeTerminalMediaUpload'])
      })
      .addCase(stripeTerminalsService.uploadStripeTerminalMedia.fulfilled, (state, action) => {
        return receivePostPutItemData<StripeTerminalsState, any>(
          { ...state },
          ['stripeTerminalMediaUpload'],
          action.payload,
        )
      })
      .addCase(stripeTerminalsService.uploadStripeTerminalMedia.rejected, (state, action) => {
        return errorPostPutItemData<StripeTerminalsState, any>(
          { ...state },
          ['stripeTerminalMediaUpload'],
          String(action.error.message),
        )
      })

      // delete stripe terminal media
      .addCase(stripeTerminalsService.deleteStripeTerminalMedia.pending, (state) => {
        return requestDeleteItem<StripeTerminalsState, any>({ ...state }, ['stripeTerminalMediaDelete'])
      })
      .addCase(stripeTerminalsService.deleteStripeTerminalMedia.fulfilled, (state) => {
        return receiveDeleteItem<StripeTerminalsState, any>({ ...state }, ['stripeTerminalMediaDelete'])
      })
      .addCase(stripeTerminalsService.deleteStripeTerminalMedia.rejected, (state, action) => {
        return errorDeleteItem<StripeTerminalsState, any>(
          { ...state },
          ['stripeTerminalMediaDelete'],
          String(action.error.message),
        )
      })
  },
})

export const { updateStripeTerminals } = stripeTerminalsSlice.actions

export default stripeTerminalsSlice.reducer
